import React, { FunctionComponent, ReactNode, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Col, Flex } from 'antd';
import {
  CheckOutlined,
  CloseCircleOutlined,
  InfoCircleOutlined,
} from '@ant-design/icons';
import { useStore } from '@hub/helpers/use-store';
import { Card } from '@monorepo/react-components';
import { ConsistencyRuleResult, ConsistencyRules } from '@monorepo/types';
import ProfitableDaysModal from './profitable-days-modal/profitable-days-modal';

import './consistency-rule.scss';

interface Props {
  rule: ConsistencyRules;
  ruleResult: ConsistencyRuleResult;
  programId: string;
  accountId: string;
}

const ConsistencyRule: FunctionComponent<Props> = ({
  rule,
  programId,
  accountId,
  ruleResult,
}) => {
  const {
    dataStore: { programStore },
  } = useStore();
  const [modalOpen, setModalOpen] = useState<boolean>(false);
  const { t } = useTranslation(['dashboardTemplates']);

  const program = programStore.get(programId);

  const programRule = program?.config.consistencyRules?.find(
    (configRule) => configRule.rule === rule,
  );

  if (!programRule) {
    return null;
  }

  const getModalByRule = () => {
    const modalsMap: { [key: string]: ReactNode } = {
      [ConsistencyRules.ProfitableDays]: (
        <ProfitableDaysModal
          accountId={accountId}
          close={() => setModalOpen(false)}
          open={modalOpen}
        />
      ),
    };

    const modal = modalsMap[rule as ConsistencyRules];

    return modal || null;
  };

  const RulesToTitle = {
    [ConsistencyRules.BestTradeProfitLimit]: () =>
      t('dashboardTemplates:consistency_rules.best_trade_profit_limit'),
    [ConsistencyRules.ProfitableDays]: () =>
      t('dashboardTemplates:consistency_rules.profitable_days'),
    [ConsistencyRules.MinimumTrades]: () =>
      t('dashboardTemplates:consistency_rules.minimum_trades'),
    [ConsistencyRules.SymbolConsistency]: () =>
      t('dashboardTemplates:consistency_rules.symbol_consistency', {
        defaultValue: 'Symbol Consistency',
      }),
    [ConsistencyRules.ProfitableMonths]: () =>
      t('dashboardTemplates:consistency_rules.profitable_months', {
        defaultValue: 'Profitable Months',
      }),
  };

  const RulesToResult = {
    [ConsistencyRules.BestTradeProfitLimit]: (value: number) =>
      `${Math.round(value)}%`,
    [ConsistencyRules.ProfitableDays]: (value: number) => value,
    [ConsistencyRules.MinimumTrades]: (value: number) => value,
    [ConsistencyRules.SymbolConsistency]: (value: number) => value,
    [ConsistencyRules.ProfitableMonths]: (value: number) => value,
  };

  const RulesToText = {
    [ConsistencyRules.BestTradeProfitLimit]: (value: number) =>
      t('dashboardTemplates:consistency_rules.best_trade_profit_limit_text', {
        value,
      }),
    [ConsistencyRules.ProfitableDays]: (value: number) =>
      t('dashboardTemplates:consistency_rules.should_have_profitable_days', {
        value,
      }),
    [ConsistencyRules.MinimumTrades]: (value: number) =>
      t('dashboardTemplates:consistency_rules.should_have_trades', {
        value,
      }),
    [ConsistencyRules.SymbolConsistency]: (value: number) =>
      t('dashboardTemplates:consistency_rules.should_have_symbols', {
        defaultValue: `Should have at least ${value} profitable symbols`,
        value,
      }),
    [ConsistencyRules.ProfitableMonths]: (value: number) => {
      const secondaryValue = programRule?.secondaryValue;
      if (secondaryValue !== undefined) {
        return t(
          'dashboardTemplates:consistency_rules.should_have_profitable_months',
          {
            defaultValue: `Should have at least ${value} rolling 30-day periods with at least ${secondaryValue}% profit each`,
            value,
            secondaryValue,
          },
        );
      }
      return t(
        'dashboardTemplates:consistency_rules.should_have_profitable_months',
        {
          defaultValue: `Should have at least ${value} profitable rolling 30-day periods`,
          value,
        },
      );
    },
  };

  const RuleHasExtraInfo = [ConsistencyRules.ProfitableDays];

  const ruleTextFn = RulesToText[rule as ConsistencyRules];
  const ruleText = ruleTextFn(programRule?.value);
  const ruleTitle = RulesToTitle[rule as ConsistencyRules]();
  const ruleValue = RulesToResult[rule as ConsistencyRules](ruleResult.current);
  const hasModal = RuleHasExtraInfo.includes(rule);

  const title = (
    <Flex justify="space-between">
      <span>
        {ruleTitle}
        {hasModal ? (
          <Button shape="circle" onClick={() => setModalOpen(true)} type="text">
            <InfoCircleOutlined />
          </Button>
        ) : null}
      </span>
      <span>{ruleValue}</span>
    </Flex>
  );

  return (
    <>
      <Col className="consistency-rule" span={24} key={rule}>
        <Card
          bordered={false}
          title={title}
          bodyStyle={{
            display: 'flex',
            justifyContent: 'space-between',
          }}
        >
          <span>
            {ruleResult.isValid ? (
              <CheckOutlined style={{ color: 'green' }} />
            ) : (
              <CloseCircleOutlined style={{ color: 'red' }} />
            )}
          </span>
          <span style={{ fontSize: '12px', maxWidth: '200px' }}>
            {ruleText}
          </span>
        </Card>
      </Col>
      {getModalByRule()}
    </>
  );
};

export default ConsistencyRule;
